import { toaster } from '@app/components/ui/toaster'
import track from '@app/lib/analyticsHelper'
import { useStore } from '@app/store'
import { protocolifyLink } from '@app/utils/stringHelpers'
import { MetricCreate, MetricUpdate } from '@graphql/documents/metric.graphql'
import type {
  MetricCreateMutation,
  MetricCreateMutationVariables,
  MetricUpdateMutation,
  MetricUpdateMutationVariables
} from '@graphql/queries'
import type { MetricCreateInput, MetricUpdateInput, User } from '@graphql/types'

const parseMetricInput = (input) => {
  const metricInput = { ...input }

  metricInput.minimumDisplayPrecision &&= parseInt(metricInput.minimumDisplayPrecision, 10)
  metricInput.maximumDisplayPrecision &&= parseInt(metricInput.maximumDisplayPrecision, 10)
  metricInput.externalUrl &&= protocolifyLink(metricInput.externalUrl)
  metricInput.labels &&= JSON.parse(metricInput.labels)
  if (metricInput.confidenceRating === '') {
    metricInput.confidenceRating = null
  }

  delete metricInput['slack-channel-autocomplete']

  return metricInput
}

type CreateMetric = (
  metricInput: MetricCreateInput,
  user: Pick<User, 'optedInToAnalytics'>
) => Promise<MetricCreateMutation['metricCreate']['metric']>
export const createMetric: CreateMetric = async (metricInput, user) => {
  const input = parseMetricInput(metricInput)
  const { actionMutation } = useStore.getState()

  return actionMutation<MetricCreateMutation, MetricCreateMutationVariables>(MetricCreate, input).then((resp) => {
    track('metric:created', input, user)

    if (resp.error) {
      throw resp.error
    }

    const { metric, errors } = resp.data.metricCreate

    if (errors.length) {
      throw errors
    }

    toaster.create({
      title: 'Creating your metric',
      type: 'success'
    })

    return metric
  })
}

type UpdateMetric = (
  metricId: string,
  metricInput: Omit<MetricUpdateInput, 'metricId'>,
  user: Pick<User, 'optedInToAnalytics'>
) => Promise<MetricUpdateMutation['metricUpdate']['metric']>
export const updateMetric: UpdateMetric = async (metricId, metricInput, user) => {
  const input = { ...metricInput, metricId }
  const parsedInput = parseMetricInput(input)
  const { actionMutation } = useStore.getState()

  return actionMutation<MetricUpdateMutation, MetricUpdateMutationVariables>(MetricUpdate, parsedInput).then((resp) => {
    track('metric:updated', input, user)

    if (resp.error) {
      throw resp.error
    }

    const { metric, errors } = resp.data.metricUpdate

    if (errors.length) {
      throw errors
    }

    toaster.create({
      title: 'Updating your metric',
      type: 'success'
    })

    return metric
  })
}
